import React, { useState } from 'react';
import { Tag } from 'lucide-react';
import {
  Utterance,
  EntityType,
  EntitySentiment,
  Entity,
} from '../../../types/annotation';
import EmotionSelector from '../labels/EmotionSelector';
import SentimentSelector from '../labels/SentimentSelector';
import IntentSelector from '../labels/IntentSelector';
import SpeechSpeedSelector from '../labels/SpeechSpeedSelector';
import AudioQualitySelector from '../labels/AudioQualitySelector';
import SpeechMarkerCheckboxes from '../utterance/SpeechMarkerCheckboxes';
import EntityList from '../entity/EntityList';
import EntityTagModal from '../entity/EntityTagModal';

interface AnnotationPanelProps {
  utterance: Utterance | null;
  isReviewMode: boolean;
  onUpdate: (utteranceId: string, updates: Partial<Utterance>) => void;
  onAddEntity: (
    utteranceId: string,
    data: { entityType: EntityType; entityName: string; sentiment?: EntitySentiment },
  ) => Promise<void>;
  onDeleteEntity: (utteranceId: string, entityId: string) => Promise<void>;
  allEntityNames: string[];
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 border-b border-gray-100 pb-1">
      {title}
    </p>
    {children}
  </div>
);

const AnnotationPanel: React.FC<AnnotationPanelProps> = ({
  utterance,
  isReviewMode,
  onUpdate,
  onAddEntity,
  onDeleteEntity,
  allEntityNames,
}) => {
  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | undefined>(undefined);

  if (!utterance) {
    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mb-3">
            <Tag className="w-5 h-5 text-violet-500" />
          </div>
          <p className="text-sm font-medium text-gray-600">Select a segment to annotate</p>
          <p className="text-xs text-gray-400 mt-1">
            Click on a segment in the transcript panel to label it.
          </p>
        </div>
      </div>
    );
  }

  const disabled = isReviewMode;

  const handleEntitySave = async (data: {
    entityType: EntityType;
    entityName: string;
    sentiment?: EntitySentiment;
  }) => {
    setEntityModalOpen(false);
    setEditingEntity(undefined);
    await onAddEntity(utterance.utteranceId, data);
  };

  const handleEntityEdit = (entity: Entity) => {
    setEditingEntity(entity);
    setEntityModalOpen(true);
  };

  const handleEntityDelete = async (entityId: string) => {
    await onDeleteEntity(utterance.utteranceId, entityId);
  };

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-700">Annotation</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Segment #{utterance.sequenceIndex + 1}
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Emotion */}
        <Section title="Emotion">
          <EmotionSelector
            value={utterance.emotion}
            onChange={(v) => onUpdate(utterance.utteranceId, { emotion: v })}
            disabled={disabled}
          />
        </Section>

        {/* Sentiment */}
        <Section title="Sentiment">
          <SentimentSelector
            value={utterance.sentiment}
            onChange={(v) => onUpdate(utterance.utteranceId, { sentiment: v })}
            disabled={disabled}
          />
        </Section>

        {/* Conversational Intent */}
        <Section title="Conversational Intent">
          <IntentSelector
            value={utterance.intent}
            onChange={(v) => onUpdate(utterance.utteranceId, { intent: v })}
            disabled={disabled}
          />
        </Section>

        {/* Speech Speed */}
        <Section title="Speech Speed">
          <SpeechSpeedSelector
            value={utterance.speechSpeed}
            onChange={(v) => onUpdate(utterance.utteranceId, { speechSpeed: v })}
            disabled={disabled}
          />
        </Section>

        {/* Audio Quality */}
        <Section title="Audio Quality">
          <AudioQualitySelector
            value={utterance.audioQuality}
            onChange={(v) => onUpdate(utterance.utteranceId, { audioQuality: v })}
            disabled={disabled}
          />
        </Section>

        {/* Speech Markers */}
        <Section title="Speech Markers">
          <SpeechMarkerCheckboxes
            value={utterance.speechMarkers}
            onChange={(markers) =>
              onUpdate(utterance.utteranceId, { speechMarkers: markers })
            }
            disabled={disabled}
          />
        </Section>

        {/* Entities */}
        <Section title="Entities">
          <div className="space-y-2">
            <EntityList
              entities={utterance.entities}
              onDelete={handleEntityDelete}
              onEdit={!disabled ? handleEntityEdit : undefined}
              disabled={disabled}
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => {
                  setEditingEntity(undefined);
                  setEntityModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium"
                title="Add entity (Ctrl+E)"
              >
                <Tag className="w-3.5 h-3.5" />
                Add Entity
                <kbd className="ml-1 text-xs font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-gray-400">
                  Ctrl+E
                </kbd>
              </button>
            )}
          </div>
        </Section>
      </div>

      {/* Entity modal */}
      <EntityTagModal
        open={entityModalOpen}
        onClose={() => {
          setEntityModalOpen(false);
          setEditingEntity(undefined);
        }}
        onSave={handleEntitySave}
        initialData={editingEntity}
        suggestions={allEntityNames}
      />
    </div>
  );
};

export default AnnotationPanel;
